import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetAccumulatedChartData, useGetAssignedChannelOptions, useGetDistribution, useGetLeadsPipeline, useGetLeadsSummary, useGetMetricsFromAssignedChannel, useGetMetricsFromSourceChannel, useGetSourceChannelOptions } from "../../../api/leadsManagement";
import { iconMapping } from "../../../helpers/leadsManagement";
import { usePermissionStore } from "../../../stores/permissionStore";
import { ChartData, LeadAnalyticsData } from "../../../types/leads.types";



const useAnalyticsData = () => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);

    const { mutate: searchLeadsMutation, data: dashboardSummary, isLoading: isLoadingDashboardSummary } = useGetLeadsSummary();

    const leadSummary = dashboardSummary?.data;

    const {
        mutate: getPipelineDataMutate,
        data: pipelinesData,
        isLoading: isLoadingPipeline,
        isError: hasPipelineDataError
    } = useGetLeadsPipeline();
    const pipelineGraphData = pipelinesData?.data?.leadPipelines;

    const {
        mutate: getDistributionDataMutate,
        data: distributionData,
        isLoading: isLoadingDistribution,
        isError: hasDistributionDataError
    } = useGetDistribution();
    const distributionGraphData = distributionData?.data?.leadTypes;

    const {
        mutate: getMetricsFromSourceChannelMutate,
        data: metricsBySourceChannelData,
        isLoading: isLoadingMetricsBySourceChannel,
        isError: hasMetricsBySourceChannelError
    } = useGetMetricsFromSourceChannel();

    const {
        mutate: getMetricsFromAssignedChannelMutate,
        data: metricsByAssignedChannelData,
        isLoading: isLoadingMetricsByAssignedChannel,
        isError: hasMetricsByAssignedChannelError
    } = useGetMetricsFromAssignedChannel();

    const {
        mutate: getAccumulatedDataMutate,
        data: accumulatedData,
        isLoading: isLoadingAccumulatedData,
        isError: hasAccumulatedChartDataError
    } = useGetAccumulatedChartData();

    const apeBySourceChannelData = useMemo(() => {
        if (!metricsBySourceChannelData?.data?.ape) {
            return null;
        }

        return metricsBySourceChannelData.data.ape;
    }, [isLoadingMetricsBySourceChannel, metricsBySourceChannelData])

    const apeByAssignedChannelData = useMemo(() => {
        if (!metricsByAssignedChannelData) {
            return null
        }
        return metricsByAssignedChannelData?.data?.ape

    }, [isLoadingMetricsByAssignedChannel, metricsByAssignedChannelData])

    const wonBySourceChannelData = useMemo(() => {
        if (isLoadingMetricsBySourceChannel || !metricsBySourceChannelData?.data?.wonDeals) {
            return null;
        }

        return metricsBySourceChannelData.data.wonDeals;
    }, [isLoadingMetricsBySourceChannel, metricsBySourceChannelData])

    const wonByAssignedChannelData = useMemo(() => {
        if (!metricsByAssignedChannelData) {
            return null
        }

        return metricsByAssignedChannelData?.data?.wonDeals

    }, [isLoadingMetricsByAssignedChannel, metricsByAssignedChannelData])

    // Common function to process APE and Won Deals data
    const processWonAndAPEData = useCallback((metricsData: any) => {
        if (!metricsData?.data?.ape || !metricsData?.data?.wonDeals) {
            return {
                xLabels: [],
                series: []
            };
        }

        const apeData = metricsData.data.ape;
        const wonDealsData = metricsData.data.wonDeals;

        // Find common labels between APE and wonDeals
        const apeLabels = apeData.map((item: ChartData) => item.label);
        const wonDealsLabels = wonDealsData.map((item: ChartData) => item.label);
        const commonLabels = apeLabels.filter((label: string) => wonDealsLabels.includes(label));

        // Extract data arrays for each series based on common labels
        const apeDataArray = commonLabels.map((label: string) => {
            const item = apeData.find((d: ChartData) => d.label === label);
            return item?.value || 0;
        });

        const wonDealsDataArray = commonLabels.map((label: string) => {
            const item = wonDealsData.find((d: ChartData) => d.label === label);
            return item?.value || 0;
        });

        // Create series configuration
        const series = [
            {
                data: apeDataArray,
                id: "apeData",
                label: "APE",
                color: "#e87722" // Default color, can be overridden
            },
            {
                data: wonDealsDataArray,
                id: "wonDealsData",
                label: "Won Deals",
                color: "#4cb66b" // Default color, can be overridden
            }
        ];

        return {
            xLabels: commonLabels,
            series: series
        };
    }, []);

    const wonAndAPEFromSourceChannel = useMemo(() => {
        return processWonAndAPEData(metricsBySourceChannelData);
    }, [metricsBySourceChannelData, processWonAndAPEData]);

    const wonAndAPEFromAssignedChannel = useMemo(() => {
        return processWonAndAPEData(metricsByAssignedChannelData);
    }, [metricsByAssignedChannelData, processWonAndAPEData]);

    // Convert qualification data for StackBarChart
    const stackedQualificationfromSourceChannelData = useMemo(() => {
        if (!metricsBySourceChannelData?.data?.qualification) {
            return {
                series: [
                    { data: [], id: "hotDataId", stack: "total", label: "Hot" },
                    { data: [], id: "warmDataId", stack: "total", label: "Warm" },
                    { data: [], id: "coldDataId", stack: "total", label: "Cold" }
                ],
                xLabels: []
            };
        }
        const qualificationData = metricsBySourceChannelData.data.qualification;

        // Extract labels from the array
        const channels = qualificationData.map((item: any) => item.label);
        
        // Extract data for each temperature by finding the matching item
        const warmData = channels.map((channel: string) => {
            const item = qualificationData.find((d: any) => d.label === channel);
            return item?.value?.warm || 0;
        });
        const hotData = channels.map((channel: string) => {
            const item = qualificationData.find((d: any) => d.label === channel);
            return item?.value?.hot || 0;
        });
        const coldData = channels.map((channel: string) => {
            const item = qualificationData.find((d: any) => d.label === channel);
            return item?.value?.cold || 0;
        });

        const series = [
            {
                data: hotData, id: "hotDataId", stack: "total", label: "Hot"
            },
            {
                data: warmData, id: "warmDataId", stack: "total", label: "Warm"
            },
            {
                data: coldData, id: "coldDataId", stack: "total", label: "Cold"
            },
        ]

        return {
            series,
            xLabels: channels
        };
    }, [metricsBySourceChannelData]);


    const stackedQualificationfromAssignedChannelData = useMemo(() => {
        if (!metricsByAssignedChannelData?.data?.qualification) {
            return {
                series: [
                    { data: [], id: "hotDataId", stack: "total", label: "Hot" },
                    { data: [], id: "warmDataId", stack: "total", label: "Warm" },
                    { data: [], id: "coldDataId", stack: "total", label: "Cold" }
                ],
                xLabels: []
            };
        }
        const qualificationData = metricsByAssignedChannelData.data.qualification;

        // Extract labels from the array
        const channels = qualificationData.map((item: any) => item.label);
        
        // Extract data for each temperature by finding the matching item
        const warmData = channels.map((channel: string) => {
            const item = qualificationData.find((d: any) => d.label === channel);
            return item?.value?.warm || 0;
        });
        const hotData = channels.map((channel: string) => {
            const item = qualificationData.find((d: any) => d.label === channel);
            return item?.value?.hot || 0;
        });
        const coldData = channels.map((channel: string) => {
            const item = qualificationData.find((d: any) => d.label === channel);
            return item?.value?.cold || 0;
        });

        const series = [
            {
                data: hotData, id: "hotDataId", stack: "total", label: "Hot"
            },
            {
                data: warmData, id: "warmDataId", stack: "total", label: "Warm"
            },
            {
                data: coldData, id: "coldDataId", stack: "total", label: "Cold"
            },
        ]

        return {
            series,
            xLabels: channels
        };
    }, [metricsByAssignedChannelData]);



    const accumulatedGraphData = useMemo(() => {
        if (!accumulatedData?.data?.accumulationCount) {
            return {
                xAxisData: [],
                yAxisData: []
            };
        }

        const xAxisData: string[] = [];
        const yAxisData: number[] = [];

        accumulatedData.data.accumulationCount.forEach((item: { x: string; y: number }) => {
            xAxisData.push(item.x);
            yAxisData.push(item.y);
        });

        return {
            xAxisData,
            yAxisData
        };
    }, [accumulatedData]);

    const { data: sourceChannelOptions } = useGetSourceChannelOptions({
        permission_Token: usePermissionStore.getState().permissionToken ?? ""
    });

    const { data: assignedChannelOptions } = useGetAssignedChannelOptions({
        permission_Token: usePermissionStore.getState().permissionToken ?? ""
    });

    const formMethods = useForm<LeadAnalyticsData>({
        defaultValues: { filterSourceChannels: "All Channels", filterAssignedChannels: "All Channels", startDate: "", endDate: "" },
    });

    const { setValue } = formMethods;

    // Initial load with default values
    useEffect(() => {
        const defaultParams = {
            permission_Token: usePermissionStore.getState().permissionToken ?? "",
            filterSourceChannels: [],
            filterAssignedChannels: [],
            filterCountries: [],
            startDate: "",
            endDate: ""
        };

        searchLeadsMutation(defaultParams);
        getPipelineDataMutate(defaultParams);
        getDistributionDataMutate(defaultParams);
        getMetricsFromSourceChannelMutate(defaultParams);
        getMetricsFromAssignedChannelMutate(defaultParams);
        getAccumulatedDataMutate(defaultParams);
    }, []);

    // Format dates for API usage (ISO format with time)
    const formattedStartDate = useMemo(() => {
        if (!startDate) return "";
        // Set to start of day (00:00:00) for start date
        return startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
    }, [startDate]);

    const formattedEndDate = useMemo(() => {
        if (!endDate) return "";
        // Set to end of day (23:59:59) for end date
        return endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
    }, [endDate]);

    // performSearch function for manual API calls
    const performSearch = (overrides = {}) => {
        const currentFormValue = formMethods.getValues();

        // Merge current values with any overrides
        const searchParams = {
            filterSourceChannels: currentFormValue.filterSourceChannels,
            filterAssignedChannels: currentFormValue.filterAssignedChannels,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            ...overrides
        };

        const apiParams = {
            permission_Token: usePermissionStore.getState().permissionToken ?? "",
            filterSourceChannels: searchParams.filterSourceChannels === "All Channels" ? [] :
                searchParams.filterSourceChannels ? [searchParams.filterSourceChannels] : [],
            filterAssignedChannels: searchParams.filterAssignedChannels === "All Channels" ? [] :
                searchParams.filterAssignedChannels ? [searchParams.filterAssignedChannels] : [],
            filterCountries: [],
            startDate: searchParams.startDate,
            endDate: searchParams.endDate
        };

        searchLeadsMutation(apiParams);
        getPipelineDataMutate(apiParams);
        getDistributionDataMutate(apiParams);
        getMetricsFromSourceChannelMutate(apiParams);
        getMetricsFromAssignedChannelMutate(apiParams);
        getAccumulatedDataMutate(apiParams);
    };

    const sourceChannelOptionsArray = useMemo(() => {
        const defaultOption = { label: "All Channels", value: "All Channels" };
        const mappedOptions = (sourceChannelOptions?.data?.options || []).map((option: string) => ({
            label: option,
            value: option
        }));
        return [defaultOption, ...mappedOptions];
    }, [sourceChannelOptions]);

    const assignedChannelOptionsArray = useMemo(() => {
        const defaultOption = { label: "All Channels", value: "All Channels" };
        const mappedOptions = (assignedChannelOptions?.data?.options || []).map((option: string) => ({
            label: option,
            value: option
        }));
        return [defaultOption, ...mappedOptions];
    }, [assignedChannelOptions]);

    const overviewData = useMemo(() => {

        if (!leadSummary || isLoadingDashboardSummary) return []

        return Object.entries(leadSummary).map(([key, value]) => ({
            label: t(`platform.${key}`) || key,
            value: key === "conversionRate" ? `${((value as number) * 100).toFixed(1)}%` : String(value),
            icon: iconMapping(key) || <BookmarksOutlinedIcon sx={{ marginBottom: "24px" }} />
        }));

    }, [leadSummary, isLoadingDashboardSummary, t])

    const onSourceChannelChange = (val: string) => {
        setValue("filterSourceChannels", val);
        performSearch({ filterSourceChannels: val });
    }

    const onAssignedChannelChange = (val: string) => {
        setValue("filterAssignedChannels", val);
        performSearch({ filterAssignedChannels: val });
    }

    const onStartDateChange = (newDate: Dayjs | null) => {
        // Handle null date (clearing the field)
        if (newDate === null) {
            setStartDate(null);
            setValue("startDate", "");
            // Pass empty string for start date
            performSearch({ startDate: "" });
            return;
        }

        // Check if newDate is valid
        if (!newDate.isValid()) {
            console.error("Invalid date provided to onStartDateChange:", newDate);
            return;
        }

        // Validate that the date is not greater than today
        if (newDate.isAfter(dayjs(), "day")) {
            console.warn("Start date cannot be in the future");
            return;
        }

        // Set the date
        setStartDate(newDate);
        setValue("startDate", newDate.format("DD/MM/YYYY HH:mm:ss"));

        // Format the new date for API call
        const newFormattedStartDate = newDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss");

        // If end date exists and is before the new start date, clear end date
        if (endDate && endDate.isValid() && endDate.isBefore(newDate, "day")) {
            setEndDate(null);
            setValue("endDate", "");
            // Pass both new start date and empty end date
            performSearch({
                startDate: newFormattedStartDate,
                endDate: ""
            });
        } else {
            // Pass the new start date, keep existing end date
            performSearch({ startDate: newFormattedStartDate });
        }
    };

    const onEndDateChange = (newDate: Dayjs | null) => {

        // Handle null date (clearing the field)
        if (newDate === null) {
            setEndDate(null);
            setValue("endDate", "");
            // Pass empty string for end date
            performSearch({ endDate: "" });
            return;
        }

        // Check if newDate is valid
        if (!newDate.isValid()) {
            console.error("Invalid date provided to onEndDateChange:", newDate);
            return;
        }

        // Validate that the date is not greater than today
        if (newDate.isAfter(dayjs(), "day")) {
            console.warn("End date cannot be in the future");
            return;
        }

        // Validate that end date is not before start date
        if (startDate && startDate.isValid() && newDate.isBefore(startDate, "day")) {
            console.warn("End date cannot be before start date");
            return;
        }

        // Set the date
        setEndDate(newDate);
        setValue("endDate", newDate.format("DD/MM/YYYY HH:mm:ss"));

        // Format the new date for API call
        const newFormattedEndDate = newDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss");

        // Pass the new end date to performSearch
        performSearch({ endDate: newFormattedEndDate });
    };

    return {
        overviewData,
        pipelineGraphData,
        distributionGraphData,
        stackedQualificationfromSourceChannelData,
        stackedQualificationfromAssignedChannelData,
        accumulatedGraphData,
        isLoadingDashboardSummary,
        isLoadingPipeline,
        isLoadingDistribution,
        isLoadingMetricsBySourceChannel,
        isLoadingMetricsByAssignedChannel,
        isLoadingAccumulatedData,
        sourceChannelOptionsArray,
        assignedChannelOptionsArray,
        startDate,
        endDate,
        formattedStartDate,
        formattedEndDate,
        onStartDateChange,
        onEndDateChange,
        formMethods,
        onSourceChannelChange,
        onAssignedChannelChange,
        apeBySourceChannelData,
        apeByAssignedChannelData,
        wonBySourceChannelData,
        wonByAssignedChannelData,
        hasMetricsByAssignedChannelError,
        hasMetricsBySourceChannelError,
        hasAccumulatedChartDataError,
        hasDistributionDataError,
        hasPipelineDataError,
        wonAndAPEFromSourceChannel,
        wonAndAPEFromAssignedChannel,
        processWonAndAPEData,
    }
}

export default useAnalyticsData;